/**
 * Verdict — the door's read of one scanned ticket. Pure state: no Firestore
 * import lives here (the io-in-view-or-state guard) — verdict.sync.ts
 * resolves every field to a plain value before writing it here, a Firestore
 * Timestamp included (carried as epoch millis, never the SDK's own Timestamp
 * type). `scannedToken$` and the five operator-tap channels below are the
 * ONLY way a view can influence a verdict; verdict.sync.ts is the sole
 * subscriber to all of them and the sole writer of `verdictState` — no
 * exported action here writes it directly, `dismissRequested$` included — so
 * no view can ever set an optimistic verdict directly. See verdict.sync.ts's
 * header for why that makes an optimistic render structurally impossible,
 * not just avoided by convention.
 *
 * `already-in`, `stop:"wrong-type"` and `stop:"wrong-origin"` are the only
 * terminal kinds that can be constructed at zero network hops, from the local
 * ticket index (tickets.state.ts) — `already-in` only while that index's own
 * listener confirms it is live, and `wrong-origin` only while the indexed
 * ticket actually carries an `origin` (verdict.sync.ts's `resolveZeroHop`
 * carries why). Every other terminal kind (`admit`, `stop:"refunded"`,
 * `stop:"unknown"`, `held`) is constructed only from a server-acked read or
 * write — verdict.sync.ts's header carries the full accounting.
 *
 * `scannedHolder` is a second cell with a second writer
 * (`scanner/src/verdict/handover.sync.ts`), deliberately outside the
 * `Verdict` union: no member of that union carries a holder, so the door's
 * decision is constructed and rendered from the ticket alone and can never
 * wait on — or be refused by — the handover read that names the holder.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import type { Ticket } from "@momo/types";
import { Subject } from "rxjs";

export type ResolvedTicket = {
	readonly ticketId: string;
	readonly typeId: string;
	readonly orderId: string;
	/** The provenance axis this door filters on, carried from the ticket
	 *  itself — nullish for a ticket minted before `Ticket.origin` existed. */
	readonly origin: Ticket.Ticket["origin"];
};

export type PartyContext = {
	readonly total: number;
	readonly othersRemaining: number;
	readonly othersAlreadyIn: number;
};

export type Verdict =
	| { readonly kind: "confirming"; readonly slow: boolean }
	| {
			readonly kind: "admit";
			readonly ticket: ResolvedTicket;
			readonly party: PartyContext | null;
	  }
	| {
			/** Manual mode's irreversibility brake: the redeem write has NOT
			 *  happened yet — this ticket WOULD admit, pending one explicit
			 *  tap (`commitPendingAdmit$`). */
			readonly kind: "pending-admit";
			readonly ticket: ResolvedTicket;
			readonly party: PartyContext | null;
	  }
	| {
			readonly kind: "already-in";
			readonly ticket: ResolvedTicket;
			readonly self: boolean;
			readonly redeemedAtMillis: number | null;
			/** The door that redeemed it, resolved to a label — never a uid.
			 *  `null` when `self` (naming your own door back to you is a
			 *  signal the operator's own context already carries) or when no
			 *  door-labels lookup is wired yet. */
			readonly doorLabel: string | null;
	  }
	| {
			readonly kind: "stop";
			// "wrong-event" is not a member: an out-of-event ticket is absent
			// from an event-scoped query, indistinguishable from a forgery —
			// it collapses into "unknown".
			readonly reason:
				| "wrong-type"
				| "wrong-origin"
				| "refunded"
				| "unknown"
				| "too-late";
			readonly detail: string;
	  }
	| {
			readonly kind: "held";
			readonly cause: "offline" | "timeout";
			/** The scanned token the held answer belongs to, where the caller
			 *  needs to retry or reconcile it. */
			readonly token?: string;
	  };

export type VerdictState = {
	/** `null` == idle — no scan in flight, no verdict on screen. */
	readonly verdict: Verdict | null;
};

const initial: VerdictState = { verdict: null };
export const verdictState = state("scanner/verdict").memory(initial);

/** Who holds a scanned ticket now: the recipient name off that ticket's one
 *  live `Handover` (packages/types/src/handover.entity.ts), carried together
 *  with the ticket id it was resolved for so a name left over from the
 *  previous scan can never paint on this one. `null` is the ordinary case —
 *  most tickets were never handed to anyone — and renders nothing at all
 *  rather than an empty slot. */
export type ScannedHolder = {
	readonly ticketId: string;
	readonly name: string;
};

export const scannedHolder = state<ScannedHolder | null>(
	"scanner/scanned-holder",
).memory(null);

/** The holder name the surface currently on screen may render, or `null`.
 *  The ticket-id equality is what makes the late arrival safe: the holder
 *  read settles after its verdict, so a name only ever shows against the very
 *  ticket it was resolved for. `stop` and `held` resolve no ticket at all and
 *  therefore never name a holder. */
export const holderName = computed(() => {
	const verdict = verdictState.value.verdict;
	const holder = scannedHolder.value;
	return verdict !== null &&
		"ticket" in verdict &&
		holder?.ticketId === verdict.ticket.ticketId
		? holder.name
		: null;
});

/** scan.view.ts publishes every decoded QR payload here; verdict.sync.ts is
 *  the sole subscriber. Not a state field — a scan is an event, not a
 *  durable fact. */
export const scannedToken$ = new Subject<string>();

/** Manual mode's held-back write: the operator's explicit "Admit" tap on a
 *  `pending-admit` card. Acts on whatever `verdictState.value.verdict`
 *  currently holds — no payload needed. */
export const commitPendingAdmit$ = new Subject<void>();

/** The retry-shaped affordance HELD already carries and ALREADY-IN gains:
 *  "ask the server once more, right now, for a truthful answer about the
 *  ticket currently on screen." Re-runs the last scan for `held`; re-attempts
 *  the CAS directly against the known ticket ref for `already-in`. */
export const retryVerdict$ = new Subject<void>();

/** The supervisor correction: undoes the redemption the currently-displayed
 *  `already-in` verdict names, whichever door recorded it. */
export const unredeemTapped$ = new Subject<void>();

/** Explicit dismiss — the view's own per-verdict-kind hold timer elapsing, or
 *  an operator's manual dismiss tap. A trigger, not a direct write: folded
 *  into verdict.sync.ts's one pipeline like every other channel above, so
 *  `verdictState` keeps its single writer. */
export const dismissRequested$ = new Subject<void>();
